import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import type { RosterEntry } from '@nostos/shared';
import { listExercises, createAssignment, type ExercisePick } from '../lib/api.js';
import { rosterRepository } from '../storage/index.js';

const ATHLETE_BASE = import.meta.env.VITE_ATHLETE_BASE_URL;

interface Result {
  athleteName: string;
  url: string;
  qrDataUrl: string;
}

/** M1 assign flow: name an athlete, pick exercises, generate token + QR, save to local roster. */
export function AssignPanel({ onAssigned }: { onAssigned: () => void }) {
  const [exercises, setExercises] = useState<ExercisePick[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [athleteName, setAthleteName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    listExercises().then(setExercises).catch((e) => setError(e.message));
  }, []);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function assign() {
    setBusy(true);
    setError(null);
    try {
      const items = [...selected].map((exerciseId) => ({ exerciseId, sets: 3, reps: 10 }));
      const title = `${athleteName.trim()}'s rehab`;
      const { assignmentId, token } = await createAssignment(title, items);

      // Persist the token ↔ identity link LOCALLY (the PHI key never goes to the server).
      const now = new Date().toISOString();
      const entry: RosterEntry = {
        id: crypto.randomUUID(),
        token,
        assignmentId,
        athleteName: athleteName.trim(),
        createdAt: now,
        updatedAt: now,
      };
      await rosterRepository.upsert(entry);

      const url = `${ATHLETE_BASE}/#t=${token}`;
      const qrDataUrl = await QRCode.toDataURL(url, { width: 240, margin: 1 });
      setResult({ athleteName: entry.athleteName, url, qrDataUrl });
      setSelected(new Set());
      setAthleteName('');
      onAssigned();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const canAssign = athleteName.trim().length > 0 && selected.size > 0 && !busy;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900">Assign a protocol</h2>

      <input
        value={athleteName}
        onChange={(e) => setAthleteName(e.target.value)}
        placeholder="Athlete name (stays on this device)"
        className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />

      <ul className="mt-3 space-y-1">
        {exercises.map((ex) => (
          <li key={ex.id}>
            <label className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50">
              <input type="checkbox" checked={selected.has(ex.id)} onChange={() => toggle(ex.id)} />
              <span className="text-slate-800">{ex.name}</span>
              <span className="text-xs text-slate-400">
                {ex.bodyRegion}/{ex.category} · 3×10
              </span>
            </label>
          </li>
        ))}
        {exercises.length === 0 && (
          <li className="px-2 py-1.5 text-sm text-slate-400">No exercises — run `pnpm db:seed`.</li>
        )}
      </ul>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <button
        onClick={assign}
        disabled={!canAssign}
        className="mt-3 w-full rounded-lg bg-slate-900 py-2 text-sm font-medium text-white disabled:opacity-40"
      >
        {busy ? 'Assigning…' : 'Assign & generate QR'}
      </button>

      {result && (
        <div className="mt-4 rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-sm font-medium text-slate-900">{result.athleteName}</p>
          <img src={result.qrDataUrl} alt="Scan to open protocol" className="mx-auto mt-2" />
          <a href={result.url} target="_blank" rel="noreferrer" className="mt-1 block break-all text-xs text-blue-600">
            {result.url}
          </a>
          <p className="mt-1 text-xs text-slate-400">Scan on the athlete's phone (or open the link).</p>
        </div>
      )}
    </section>
  );
}
